import { useState, type ReactNode } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Link, useNavigate } from 'react-router-dom'
import { nowMs, withCountRu } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { Failed } from '../../ui/Load'
import { TableSkeleton } from '../../ui/Skeleton'
import { PageActions } from '../../app/PageHeader'
import { useSession } from '../auth/session'
import { StoryCircle } from './StoryCircle'
import { useSaveManyStories, useSaveStory, useStories, type Story } from './api'
import {
  STATUS_TEXT,
  STATUS_TONE,
  changedOrder,
  dragStyle,
  periodText,
  reorderStories,
  sortedStories,
  storyFailureText,
  storyStatus,
} from './model'

export interface StoriesScreenProps {
  onToast: (message: string) => void
}

const stay = (event: { stopPropagation: () => void }) => {
  event.stopPropagation()
}

function announcements(name: (id: unknown) => string): Announcements {
  return {
    onDragStart: ({ active }) => `Взяли «${name(active.id)}»`,
    onDragOver: ({ active, over }) =>
      over ? `«${name(active.id)}» над «${name(over.id)}»` : `«${name(active.id)}» вне списка`,
    onDragEnd: ({ active, over }) =>
      over ? `«${name(active.id)}» поставлена на место «${name(over.id)}»` : 'Порядок не изменён',
    onDragCancel: ({ active }) => `Перенос «${name(active.id)}» отменён`,
  }
}

const INSTRUCTIONS =
  'Чтобы поменять порядок, нажмите пробел, стрелками вверх и вниз выберите место и нажмите пробел ещё раз. Escape — отмена.'

interface SortableRowProps {
  story: Story
  href: string
  draggable: boolean
  onOpen: () => void
  children: ReactNode
}

/** Строка, которую тянут за ручку слева: по самой строке щёлкают, чтобы открыть. */
function SortableRow({ story, href, draggable, onOpen, children }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: story.id,
    disabled: !draggable,
  })
  const style = dragStyle(transform, transition, 'y')

  return (
    <tr
      ref={setNodeRef}
      style={style}
      data-href={href}
      data-dragging={isDragging || undefined}
      onClick={onOpen}
    >
      <td className="cat-grip" onClick={stay}>
        {draggable ? (
          <button
            type="button"
            className="ui-iconbtn"
            aria-label={`Перетащить «${story.titleRu}»`}
            {...attributes}
            {...listeners}
          >
            <Icon name="i-grip" />
          </button>
        ) : null}
      </td>
      {children}
    </tr>
  )
}

/**
 * Сторис на главной приложения — в порядке показа, слева направо. Порядок
 * меняют перетаскиванием, показ — переключателем; слайды, обложку и период
 * задают в карточке сторис.
 */
export function StoriesScreen({ onToast }: StoriesScreenProps) {
  const { can } = useSession()
  const navigate = useNavigate()
  const stories = useStories()
  const save = useSaveStory()
  const saveMany = useSaveManyStories()
  const canUpdate = can('stories', 'u')
  const now = nowMs()

  const [pending, setPending] = useState<Story[] | null>(null)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const all = pending ?? sortedStories(stories.data ?? [])
  const name = (id: unknown) => all.find((s) => s.id === id)?.titleRu ?? ''

  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return
    const next = sortedStories(reorderStories(all, Number(active.id), Number(over.id)))
    setPending(next)
    saveMany.mutate(changedOrder(all, next), {
      onSuccess: () => {
        onToast('Порядок на главной изменён')
      },
      onError: (error) => {
        onToast(storyFailureText(error))
      },
      onSettled: () => {
        setPending(null)
      },
    })
  }

  function toggle(story: Story, active: boolean) {
    save.mutate(
      { ...story, active },
      {
        onSuccess: () => {
          onToast(active ? 'Сторис включена' : 'Сторис выключена')
        },
        onError: (error) => {
          onToast(storyFailureText(error))
        },
      },
    )
  }

  return (
    <>
      <PageActions>
        {stories.data ? (
          <span
            className="ui-count adm-count"
            aria-label={withCountRu(all.length, ['сторис', 'сторис', 'сторис'])}
          >
            {all.length}
          </span>
        ) : null}
        <span className="spacer" />
        {can('stories', 'c') ? (
          <button
            type="button"
            className="ui-btn"
            onClick={() => {
              void navigate('/stories/new')
            }}
          >
            <Icon name="i-plus" />
            Новая сторис
          </button>
        ) : null}
      </PageActions>

      <section className="ui-table-card" aria-label="Сторис">
        {stories.isPending ? <TableSkeleton what="сторис" /> : null}
        {stories.isError ? (
          <Failed
            what="сторис"
            error={stories.error}
            onRetry={() => {
              void stories.refetch()
            }}
          />
        ) : null}

        {stories.data ? (
          <div className="ui-table-wrap">
            <table className="ui-table ui-stories">
              <colgroup>
                <col className="w-grip" />
                <col />
                <col className="w-slides" />
                <col className="w-period" />
                <col className="w-status" />
                <col className="w-on" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">
                    <span className="visually-hidden">Порядок</span>
                  </th>
                  <th scope="col">Сторис</th>
                  <th scope="col" data-align="end">
                    Слайдов
                  </th>
                  <th scope="col">Период</th>
                  <th scope="col">Статус</th>
                  <th scope="col" data-align="center">
                    Показ
                  </th>
                </tr>
              </thead>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
                accessibility={{
                  announcements: announcements(name),
                  screenReaderInstructions: { draggable: INSTRUCTIONS },
                }}
              >
                <SortableContext
                  items={all.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody>
                    {all.length === 0 ? (
                      <tr>
                        <td colSpan={6}>
                          <div className="ui-empty">
                            <Icon name="i-stories" />
                            <b>Сторис пока нет</b>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                    {all.map((story) => {
                      const href = `/stories/${story.id}`
                      const status = storyStatus(story, now)
                      return (
                        <SortableRow
                          key={story.id}
                          story={story}
                          href={href}
                          draggable={canUpdate && !saveMany.isPending}
                          onOpen={() => {
                            void navigate(href)
                          }}
                        >
                          <td>
                            <div className="ui-product">
                              <StoryCircle story={story} size={44} live={status === 'live'} />
                              <div className="txt">
                                <Link to={href} className="primary" onClick={stay}>
                                  {story.titleRu}
                                </Link>
                                <span className="secondary">
                                  {story.titleUz || (
                                    <span className="ui-tag" data-tone="warning">
                                      нет подписи на узбекском
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td data-align="end" className="num">
                            {story.slides.length}
                          </td>
                          <td>{periodText(story)}</td>
                          <td>
                            <span className="ui-status" data-tone={STATUS_TONE[status]}>
                              {STATUS_TEXT[status]}
                            </span>
                          </td>
                          <td data-align="center" onClick={stay}>
                            {canUpdate ? (
                              <label className="ui-switch" data-size="sm">
                                <input
                                  type="checkbox"
                                  role="switch"
                                  checked={story.active}
                                  aria-label={`Показывать «${story.titleRu}» в приложении`}
                                  onChange={(e) => {
                                    toggle(story, e.target.checked)
                                  }}
                                />
                              </label>
                            ) : (
                              <span className="ui-tag">{story.active ? 'вкл' : 'выкл'}</span>
                            )}
                          </td>
                        </SortableRow>
                      )
                    })}
                  </tbody>
                </SortableContext>
              </DndContext>
            </table>
          </div>
        ) : null}
      </section>
    </>
  )
}
